import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Search, Pencil, Trash2, CalendarDays, MapPin, Users } from 'lucide-react';
import { get, post, patch, del } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import Modal from './Modal';

const RSVP_STATUSES = ['going', 'maybe', 'no'];

const RSVP_STYLES = {
  going: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  maybe: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  no: 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
};

const EMPTY = {
  name: '',
  description: '',
  location: '',
  starts_at: '',
  ends_at: '',
};

const inputCls =
  'w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500';

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">{label}</span>
      {children}
    </label>
  );
}

// "2026-05-30T18:30:00" -> "2026-05-30T18:30" for datetime-local inputs
function toLocalInput(iso) {
  if (!iso) return '';
  return iso.slice(0, 16);
}

function formatWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function Events() {
  const { isPastor, isViewer } = useAuth();
  const [events, setEvents] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editing, setEditing] = useState(null); // event object or {} for new
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // RSVP detail panel
  const [detail, setDetail] = useState(null); // event being viewed
  const [rsvps, setRsvps] = useState([]);
  const [rsvpsLoading, setRsvpsLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [rsvpForm, setRsvpForm] = useState({ member_id: '', status: 'going' });
  const [rsvpSaving, setRsvpSaving] = useState(false);
  const [rsvpError, setRsvpError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (search) params.search = search;
      setEvents(await get('/api/v1/events', { params }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, 250); // debounce search
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    get('/api/v1/members')
      .then(setMembers)
      .catch(() => {});
  }, []);

  const openNew = () => {
    setForm(EMPTY);
    setFormError('');
    setEditing({});
  };

  const openEdit = (ev) => {
    setForm({
      name: ev.name || '',
      description: ev.description || '',
      location: ev.location || '',
      starts_at: toLocalInput(ev.starts_at),
      ends_at: toLocalInput(ev.ends_at),
    });
    setFormError('');
    setEditing(ev);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    const payload = {};
    Object.entries(form).forEach(([k, v]) => {
      if (v !== '' && v !== null) payload[k] = v;
    });
    try {
      if (editing.id) {
        await patch(`/api/v1/events/${editing.id}`, payload);
      } else {
        await post('/api/v1/events', payload);
      }
      setEditing(null);
      load();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (ev) => {
    if (!window.confirm(`Delete event "${ev.name}"? This also removes its RSVPs.`)) return;
    try {
      await del(`/api/v1/events/${ev.id}`);
      if (detail?.id === ev.id) setDetail(null);
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  const loadRsvps = useCallback(async (eventId) => {
    setRsvpsLoading(true);
    setRsvpError('');
    try {
      setRsvps(await get(`/api/v1/events/${eventId}/rsvps`));
    } catch (err) {
      setRsvpError(err.message);
    } finally {
      setRsvpsLoading(false);
    }
  }, []);

  const openDetail = (ev) => {
    setDetail(ev);
    setRsvps([]);
    setRsvpForm({ member_id: '', status: 'going' });
    setRsvpError('');
    loadRsvps(ev.id);
  };

  const addRsvp = async (e) => {
    e.preventDefault();
    if (!rsvpForm.member_id) {
      setRsvpError('Select a member.');
      return;
    }
    setRsvpSaving(true);
    setRsvpError('');
    try {
      await post(`/api/v1/events/${detail.id}/rsvps`, {
        member_id: rsvpForm.member_id,
        status: rsvpForm.status,
      });
      setRsvpForm({ member_id: '', status: 'going' });
      await loadRsvps(detail.id);
      load(); // refresh going counts
    } catch (err) {
      setRsvpError(err.message);
    } finally {
      setRsvpSaving(false);
    }
  };

  const memberName = (id) => {
    const m = members.find((x) => x.id === id);
    return m ? `${m.first_name} ${m.last_name}` : '—';
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">
            Events
          </h2>
          {isViewer && (
            <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 rounded-full px-2 py-0.5">
              Read-only demo
            </span>
          )}
        </div>
        {!isViewer && (
          <button
            onClick={openNew}
            className="bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg px-3 py-2 flex items-center gap-1"
          >
            <Plus size={16} /> Add event
          </button>
        )}
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
        />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search events…"
          className="w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 text-sm rounded-lg px-3 py-2 mb-3">
          {error}
        </div>
      )}

      {loading ? (
        <div className="p-8 text-center text-slate-400">Loading…</div>
      ) : events.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-10 text-center text-slate-400 flex flex-col items-center gap-2">
          <CalendarDays size={28} />
          No events yet.
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {events.map((ev) => (
            <button
              key={ev.id}
              type="button"
              onClick={() => openDetail(ev)}
              className="text-left bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 hover:border-brand-400 dark:hover:border-brand-500 transition-colors"
            >
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold text-slate-800 dark:text-slate-100">{ev.name}</h3>
                {!isViewer && (
                  <div className="flex gap-2 text-slate-400 shrink-0">
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        openEdit(ev);
                      }}
                      className="hover:text-brand-600 cursor-pointer"
                      title="Edit"
                    >
                      <Pencil size={16} />
                    </span>
                    {isPastor && (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          remove(ev);
                        }}
                        className="hover:text-red-600 cursor-pointer"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="mt-2 text-sm text-slate-500 dark:text-slate-400 space-y-1">
                <div className="flex items-center gap-1.5">
                  <CalendarDays size={14} /> {formatWhen(ev.starts_at)}
                </div>
                {ev.location && (
                  <div className="flex items-center gap-1.5">
                    <MapPin size={14} /> {ev.location}
                  </div>
                )}
                <div className="flex items-center gap-1.5">
                  <Users size={14} /> {ev.going_count} going
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Add / Edit event modal */}
      {editing && (
        <Modal
          title={editing.id ? 'Edit event' : 'Add event'}
          onClose={() => setEditing(null)}
          footer={
            <>
              <button
                onClick={() => setEditing(null)}
                className="px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg px-4 py-2"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </>
          }
        >
          <form onSubmit={save} className="space-y-3">
            {formError && (
              <div className="bg-red-50 text-red-700 text-sm rounded-lg px-3 py-2">
                {formError}
              </div>
            )}
            <Field label="Event name">
              <input
                required
                className={inputCls}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Field>
            <Field label="Location">
              <input
                className={inputCls}
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Starts">
                <input
                  type="datetime-local"
                  required
                  className={inputCls}
                  value={form.starts_at}
                  onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
                />
              </Field>
              <Field label="Ends">
                <input
                  type="datetime-local"
                  className={inputCls}
                  value={form.ends_at}
                  onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
                />
              </Field>
            </div>
            <Field label="Description">
              <textarea
                rows={3}
                className={inputCls}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </Field>
          </form>
        </Modal>
      )}

      {/* RSVP detail modal */}
      {detail && (
        <Modal
          title={detail.name}
          onClose={() => setDetail(null)}
          footer={
            <button
              onClick={() => setDetail(null)}
              className="px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
            >
              Close
            </button>
          }
        >
          <div className="space-y-4">
            <div className="text-sm text-slate-500 dark:text-slate-400 space-y-1">
              <div className="flex items-center gap-1.5">
                <CalendarDays size={14} /> {formatWhen(detail.starts_at)}
                {detail.ends_at ? ` – ${formatWhen(detail.ends_at)}` : ''}
              </div>
              {detail.location && (
                <div className="flex items-center gap-1.5">
                  <MapPin size={14} /> {detail.location}
                </div>
              )}
              {detail.description && (
                <p className="text-slate-600 dark:text-slate-300 pt-1">{detail.description}</p>
              )}
            </div>

            <div>
              <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-2">
                RSVPs
              </h4>
              {rsvpError && (
                <div className="bg-red-50 text-red-700 text-sm rounded-lg px-3 py-2 mb-2">
                  {rsvpError}
                </div>
              )}
              {rsvpsLoading ? (
                <div className="text-slate-400 text-sm py-4 text-center">Loading…</div>
              ) : rsvps.length === 0 ? (
                <div className="text-slate-400 text-sm py-4 text-center">No RSVPs yet.</div>
              ) : (
                <ul className="space-y-1.5">
                  {rsvps.map((r) => (
                    <li
                      key={r.id}
                      className="flex items-center justify-between text-sm border-b border-slate-100 dark:border-slate-700 pb-1.5"
                    >
                      <span className="text-slate-700 dark:text-slate-200">
                        {r.member_name || memberName(r.member_id)}
                      </span>
                      <span
                        className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                          RSVP_STYLES[r.status] || 'bg-slate-100'
                        }`}
                      >
                        {r.status}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {!isViewer && (
              <form
                onSubmit={addRsvp}
                className="border-t border-slate-200 dark:border-slate-700 pt-3 space-y-3"
              >
                <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                  Add RSVP
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Member">
                    <select
                      className={inputCls}
                      value={rsvpForm.member_id}
                      onChange={(e) =>
                        setRsvpForm({ ...rsvpForm, member_id: e.target.value })
                      }
                    >
                      <option value="">— Select —</option>
                      {members.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.first_name} {m.last_name}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Status">
                    <select
                      className={inputCls}
                      value={rsvpForm.status}
                      onChange={(e) =>
                        setRsvpForm({ ...rsvpForm, status: e.target.value })
                      }
                    >
                      {RSVP_STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
                <button
                  type="submit"
                  disabled={rsvpSaving}
                  className="bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg px-4 py-2 flex items-center gap-1"
                >
                  <Plus size={16} /> {rsvpSaving ? 'Saving…' : 'Add RSVP'}
                </button>
              </form>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
